import { Button, Checkbox, Field, Input, Panel, Select } from '@/components/ui';
import type { NotificationUserSettings } from '@/lib/types';
import { WEEKDAYS } from './model';

const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

/**
 * The person-level strip: the email master switch and the two summary
 * schedules. Times are `HH:MM` on the person's own clock (`d_users.timezone`).
 */
export function SettingsStrip({
  settings,
  disabled,
  onChange,
}: {
  settings: NotificationUserSettings;
  disabled: boolean;
  onChange: (next: NotificationUserSettings) => void;
}) {
  const toggleDay = (day: number) => {
    const has = settings.dailyDays.includes(day);
    const dailyDays = has
      ? settings.dailyDays.filter((value) => value !== day)
      : [...settings.dailyDays, day].sort((x, y) => x - y);
    onChange({ ...settings, dailyDays });
  };

  const setTime = (key: 'dailyTime' | 'weeklyTime', value: string) => {
    if (!TIME_PATTERN.test(value)) return;
    onChange({ ...settings, [key]: value });
  };

  return (
    <Panel className="mb-4">
      <div className="grid gap-x-6 gap-y-4 p-4 md:grid-cols-[minmax(0,14rem)_minmax(0,1fr)_minmax(0,1fr)]">
        <div className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-fg-muted">Email</span>
          <Checkbox
            label="Email notifications"
            checked={!settings.emailPaused}
            disabled={disabled}
            onChange={(event) => onChange({ ...settings, emailPaused: !event.target.checked })}
          />
        </div>

        <div className="flex min-w-0 flex-col gap-2">
          <Field label="Daily summary">
            {(props) => (
              <Input
                {...props}
                type="time"
                step={60}
                className="sm:max-w-[9rem]"
                value={settings.dailyTime}
                disabled={disabled}
                onChange={(event) => setTime('dailyTime', event.target.value)}
              />
            )}
          </Field>
          <div role="group" aria-label="Daily summary days" className="flex flex-wrap gap-1">
            {WEEKDAYS.map((day) => {
              const on = settings.dailyDays.includes(day.value);
              return (
                <Button
                  key={day.value}
                  type="button"
                  size="sm"
                  variant={on ? 'secondary' : 'ghost'}
                  aria-pressed={on}
                  aria-label={day.long}
                  disabled={disabled}
                  className={on ? 'border-hairline-strong' : 'text-fg-muted'}
                  onClick={() => toggleDay(day.value)}
                >
                  {day.short}
                </Button>
              );
            })}
          </div>
        </div>

        <div className="flex min-w-0 flex-col gap-2">
          <Field label="Weekly summary">
            {(props) => (
              <div className="grid grid-cols-2 gap-2 sm:max-w-[19rem]">
                <Select
                  {...props}
                  aria-label="Weekly summary day"
                  value={String(settings.weeklyDay)}
                  disabled={disabled}
                  onChange={(event) =>
                    onChange({ ...settings, weeklyDay: Number.parseInt(event.target.value, 10) })
                  }
                >
                  {WEEKDAYS.map((day) => (
                    <option key={day.value} value={day.value}>
                      {day.long}
                    </option>
                  ))}
                </Select>
                <Input
                  type="time"
                  step={60}
                  aria-label="Weekly summary time"
                  value={settings.weeklyTime}
                  disabled={disabled}
                  onChange={(event) => setTime('weeklyTime', event.target.value)}
                />
              </div>
            )}
          </Field>
        </div>
      </div>
    </Panel>
  );
}
